import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Autocomplete } from '../ui/autocomplete';
import TableComponent from '../TableComponent';
import Drawer from '../Drawer';
import { suppliesService, resourcesService } from '../../services/productionService';
import { formatQuantity, formatQuantityInput, parseQuantityInput } from '../../lib/utils';

const BOMEditor = forwardRef(function BOMEditor(
    { productId, bomData, onSave, onChange, hideSaveButton = false, readOnly = false, supplies: propsSupplies, resources: propsResources },
    ref
) {
    const [supplies, setSupplies] = useState(propsSupplies || []);
    const [resources, setResources] = useState(propsResources || []);
    const [bomSupplies, setBomSupplies] = useState(() => bomData?.supplies || []);
    const [bomResources, setBomResources] = useState(() => bomData?.resources || []);
    const [supplyDrawerOpen, setSupplyDrawerOpen] = useState(false);
    const [resourceDrawerOpen, setResourceDrawerOpen] = useState(false);
    const [editingSupply, setEditingSupply] = useState(null);
    const [editingResource, setEditingResource] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    const [supplyFormData, setSupplyFormData] = useState({
        supply_id: '',
        quantity_per_unit: '',
    });

    const [resourceFormData, setResourceFormData] = useState({
        resource_id: '',
        quantity_per_unit: '',
    });

    useEffect(() => {
        // Se as props foram fornecidas, usa elas. Caso contrário, carrega via API
        if (propsSupplies !== undefined && propsResources !== undefined) {
            setSupplies(propsSupplies);
            setResources(propsResources);
        } else {
            loadData();
        }
    }, [productId, propsSupplies, propsResources]);

    const loadData = async () => {
        try {
            const [suppliesRes, resourcesRes] = await Promise.all([
                suppliesService.list({ per_page: 1000 }),
                resourcesService.list({ per_page: 1000 }),
            ]);
            setSupplies(suppliesRes.data.data || suppliesRes.data);
            setResources(resourcesRes.data.data || resourcesRes.data);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    const openSupplyDrawerForNew = () => {
        if (readOnly) return;
        setEditingSupply(null);
        setSupplyFormData({
            supply_id: '',
            quantity_per_unit: '',
        });
        setSupplyDrawerOpen(true);
    };

    const openSupplyDrawerForEdit = (item) => {
        if (readOnly) return;
        setEditingSupply(item);
        setSupplyFormData({
            supply_id: item.supply_id,
            quantity_per_unit: formatQuantityInput(item.quantity_per_unit),
        });
        setSupplyDrawerOpen(true);
    };

    const openResourceDrawerForNew = () => {
        if (readOnly) return;
        setEditingResource(null);
        setResourceFormData({
            resource_id: '',
            quantity_per_unit: '',
        });
        setResourceDrawerOpen(true);
    };

    const openResourceDrawerForEdit = (item) => {
        if (readOnly) return;
        setEditingResource(item);
        setResourceFormData({
            resource_id: item.resource_id,
            quantity_per_unit: formatQuantityInput(item.quantity_per_unit),
        });
        setResourceDrawerOpen(true);
    };

    const handleSupplyFormChange = (e) => {
        if (e.target.name === 'supply_id') return;
        setSupplyFormData({
            ...supplyFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSupplySelect = (value) => {
        setSupplyFormData((prev) => ({
            ...prev,
            supply_id: value ? value.toString() : '',
        }));
    };

    const handleResourceFormChange = (e) => {
        if (e.target.name === 'resource_id') return;
        setResourceFormData({
            ...resourceFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleResourceSelect = (value) => {
        setResourceFormData((prev) => ({
            ...prev,
            resource_id: value ? value.toString() : '',
        }));
    };

    useEffect(() => {
        if (bomData) {
            setBomSupplies(bomData.supplies || []);
            setBomResources(bomData.resources || []);
        }
    }, [bomData]);

    const handleSupplySave = () => {
        const supply = supplies.find(s => s.id === parseInt(supplyFormData.supply_id));
        const item = {
            id: editingSupply?.id || Date.now(),
            supply_id: parseInt(supplyFormData.supply_id),
            supply,
            quantity_per_unit: parseQuantityInput(supplyFormData.quantity_per_unit),
        };
        const nextSupplies = editingSupply
            ? bomSupplies.map(s => s.id === editingSupply.id ? item : s)
            : [...bomSupplies, item];
        setBomSupplies(nextSupplies);
        if (onChange) onChange({ supplies: nextSupplies, resources: bomResources });
        setSupplyDrawerOpen(false);
    };

    const handleResourceSave = () => {
        const resource = resources.find(r => r.id === parseInt(resourceFormData.resource_id));
        const item = {
            id: editingResource?.id || Date.now(),
            resource_id: parseInt(resourceFormData.resource_id),
            resource,
            quantity_per_unit: parseQuantityInput(resourceFormData.quantity_per_unit),
        };
        const nextResources = editingResource
            ? bomResources.map(r => r.id === editingResource.id ? item : r)
            : [...bomResources, item];
        setBomResources(nextResources);
        if (onChange) onChange({ supplies: bomSupplies, resources: nextResources });
        setResourceDrawerOpen(false);
    };

    const handleRemoveSupply = (idOrRow) => {
        if (readOnly) return;
        const id = typeof idOrRow === 'object' && idOrRow !== null ? idOrRow.id : idOrRow;
        const nextSupplies = bomSupplies.filter(s => s.id !== id);
        setBomSupplies(nextSupplies);
        if (onChange) onChange({ supplies: nextSupplies, resources: bomResources });
    };
    const handleRemoveResource = (idOrRow) => {
        if (readOnly) return;
        const id = typeof idOrRow === 'object' && idOrRow !== null ? idOrRow.id : idOrRow;
        const nextResources = bomResources.filter(r => r.id !== id);
        setBomResources(nextResources);
        if (onChange) onChange({ supplies: bomSupplies, resources: nextResources });
    };

    const handleSave = async () => {
        if (readOnly) return;
        setFormLoading(true);
        try {
            const payload = {
                supplies: bomSupplies.map((s, idx) => ({
                    supply_id: s.supply_id,
                    quantity_per_unit: s.quantity_per_unit,
                    sort_order: idx + 1,
                })),
                resources: bomResources.map((r, idx) => ({
                    resource_id: r.resource_id,
                    quantity_per_unit: r.quantity_per_unit,
                    sort_order: idx + 1,
                })),
            };
            await onSave(payload);
        } catch (error) {
            console.error('Error saving BOM:', error);
            // Error já tratado no onSave
        } finally {
            setFormLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        handleSave,
    }));

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value || 0);
    };

    /** Mesmas larguras em Insumos e Recursos (table-fixed + colunas alinhadas) */
    const bomTableClassName = 'table-fixed w-full';
    const bomCol = {
        item: 'min-w-0 w-[34%]',
        qty: 'min-w-0 w-[14%]',
        unitCost: 'min-w-0 w-[16%] text-right tabular-nums',
        total: 'min-w-0 w-[16%] text-right tabular-nums',
    };
    const bomActionColClass = 'w-32 min-w-[8rem] max-w-[8rem]';

    const supplyColumns = [
        {
            key: 'supply',
            label: 'Item',
            colClass: bomCol.item,
            render: (value, row) => row.supply ? `${row.supply.code} - ${row.supply.description}` : '-',
        },
        {
            key: 'quantity_per_unit',
            label: 'Quantidade por Unidade',
            colClass: bomCol.qty,
            render: (value) => formatQuantity(value),
        },
        {
            key: 'unit_cost',
            label: 'Custo Unitário',
            colClass: bomCol.unitCost,
            render: (value, row) => formatCurrency(parseFloat(row.supply?.unit_cost || 0)),
        },
        {
            key: 'total_cost',
            label: 'Custo total',
            colClass: bomCol.total,
            render: (value, row) => {
                const qty = parseFloat(row.quantity_per_unit || 0);
                const unitCost = parseFloat(row.supply?.unit_cost || 0);
                return formatCurrency(qty * unitCost);
            },
        },
    ];

    const resourceColumns = [
        {
            key: 'resource',
            label: 'Item',
            colClass: bomCol.item,
            render: (value, row) => row.resource ? `${row.resource.code} - ${row.resource.description}` : '-',
        },
        {
            key: 'quantity_per_unit',
            label: 'Quantidade por Unidade',
            colClass: bomCol.qty,
            render: (value) => formatQuantity(value),
        },
        {
            key: 'unit_cost',
            label: 'Custo Unitário',
            colClass: bomCol.unitCost,
            render: (value, row) => formatCurrency(parseFloat(row.resource?.unit_cost || 0)),
        },
        {
            key: 'total_cost',
            label: 'Custo total',
            colClass: bomCol.total,
            render: (value, row) => {
                const quantity = parseFloat(row.quantity_per_unit || 0);
                const unitCost = parseFloat(row.resource?.unit_cost || 0);
                return formatCurrency(quantity * unitCost);
            },
        },
    ];

    return (
        <div className="space-y-6">
            {/* Supplies Section */}
            <div>
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-semibold leading-none tracking-tight">Insumos</h3>
                    {!readOnly && (
                        <Button type="button" onClick={openSupplyDrawerForNew}>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Adicionar Insumo
                        </Button>
                    )}
                </div>
                <TableComponent
                    columns={supplyColumns}
                    data={bomSupplies}
                    tableClassName={bomTableClassName}
                    actionColClass={bomActionColClass}
                    onEdit={readOnly ? undefined : openSupplyDrawerForEdit}
                    onDelete={readOnly ? undefined : handleRemoveSupply}
                    emptyMessage="Nenhum insumo adicionado"
                    customActions={
                        readOnly
                            ? undefined
                            : (row) => (
                                  <div className="flex items-center justify-end gap-2">
                                      <button
                                          type="button"
                                          onClick={() => openSupplyDrawerForEdit(row)}
                                          className="p-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                          title="Editar"
                                      >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                      </button>
                                      <button
                                          type="button"
                                          onClick={() => handleRemoveSupply(row.id)}
                                          className="p-2 text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg hover:bg-red-50 transition-colors"
                                          title="Remover"
                                      >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                      </button>
                                  </div>
                              )
                    }
                />
            </div>

            {/* Resources Section */}
            <div>
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-semibold leading-none tracking-tight">Recursos</h3>
                    {!readOnly && (
                        <Button type="button" onClick={openResourceDrawerForNew}>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Adicionar Recurso
                        </Button>
                    )}
                </div>
                <TableComponent
                    columns={resourceColumns}
                    data={bomResources}
                    tableClassName={bomTableClassName}
                    actionColClass={bomActionColClass}
                    onEdit={readOnly ? undefined : openResourceDrawerForEdit}
                    onDelete={readOnly ? undefined : handleRemoveResource}
                    emptyMessage="Nenhum recurso adicionado"
                    customActions={
                        readOnly
                            ? undefined
                            : (row) => (
                                  <div className="flex items-center justify-end gap-2">
                                      <button
                                          type="button"
                                          onClick={() => openResourceDrawerForEdit(row)}
                                          className="p-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                          title="Editar"
                                      >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                      </button>
                                      <button
                                          type="button"
                                          onClick={() => handleRemoveResource(row.id)}
                                          className="p-2 text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg hover:bg-red-50 transition-colors"
                                          title="Remover"
                                      >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                      </button>
                                  </div>
                              )
                    }
                />
            </div>

            {/* Save Button */}
            {!hideSaveButton && !readOnly && (
                <div className="flex justify-end">
                    <Button onClick={handleSave} disabled={formLoading}>
                        {formLoading ? 'Salvando...' : 'Salvar BOM'}
                    </Button>
                </div>
            )}

            <Drawer
                open={supplyDrawerOpen}
                onClose={() => setSupplyDrawerOpen(false)}
                title={editingSupply ? 'Editar Insumo' : 'Adicionar Insumo'}
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setSupplyDrawerOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="button" onClick={handleSupplySave}>Salvar</Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Insumo *
                        </label>
                        <Autocomplete
                            options={supplies}
                            getOptionLabel={(option) => `${option.code} - ${option.description}`}
                            getOptionValue={(option) => option.id}
                            value={supplyFormData.supply_id ? parseInt(supplyFormData.supply_id) : null}
                            onChange={handleSupplySelect}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantidade por Unidade *
                        </label>
                        <Input
                            type="number"
                            step="0.001"
                            name="quantity_per_unit"
                            value={supplyFormData.quantity_per_unit}
                            onChange={handleSupplyFormChange}
                            required
                        />
                    </div>
                </div>
            </Drawer>

            <Drawer
                open={resourceDrawerOpen}
                onClose={() => setResourceDrawerOpen(false)}
                title={editingResource ? 'Editar Recurso' : 'Adicionar Recurso'}
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setResourceDrawerOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="button" onClick={handleResourceSave}>Salvar</Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Recurso *
                        </label>
                        <Autocomplete
                            options={resources}
                            getOptionLabel={(option) => `${option.code} - ${option.description}`}
                            getOptionValue={(option) => option.id}
                            value={resourceFormData.resource_id ? parseInt(resourceFormData.resource_id) : null}
                            onChange={handleResourceSelect}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantidade por Unidade *
                        </label>
                        <Input
                            type="number"
                            step="0.001"
                            name="quantity_per_unit"
                            value={resourceFormData.quantity_per_unit}
                            onChange={handleResourceFormChange}
                            required
                        />
                    </div>
                </div>
            </Drawer>
        </div>
    );
});

export default BOMEditor;

